import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  signIn, 
  signOut, 
  getCurrentUser, 
  fetchAuthSession,
  SignInInput,
  SignInOutput,
} from 'aws-amplify/auth';
import { UserRole } from '@recruitai/shared';

interface User {
  userId: string;
  email: string;
  name: string;
  role: UserRole;
  orgId: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      
      if (currentUser && session.tokens) {
        const idToken = session.tokens.idToken;
        const payload = idToken?.payload;

        setUser({
          userId: currentUser.userId,
          email: payload?.email as string,
          name: payload?.name as string || '',
          role: payload?.['custom:role'] as UserRole || UserRole.RECRUITER,
          orgId: payload?.['custom:orgId'] as string || '',
        });
      }
    } catch (error) {
      console.log('Not authenticated', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const signInInput: SignInInput = {
        username: email,
        password,
      };

      const result: SignInOutput = await signIn(signInInput);

      if (result.isSignedIn) {
        await checkAuthStatus();
      } else {
        throw new Error('Sign in not completed');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      const msg = error.message || '';
      // Translate Cognito/Amplify error messages to pt-BR
      if (msg.includes('Incorrect username or password') || msg.includes('NotAuthorizedException')) {
        throw new Error('Email ou senha incorretos');
      } else if (msg.includes('User does not exist') || msg.includes('UserNotFoundException')) {
        throw new Error('Email ou senha incorretos');
      } else if (msg.includes('Password attempts exceeded') || msg.includes('LimitExceededException')) {
        throw new Error('Muitas tentativas. Aguarde alguns minutos e tente novamente.');
      } else if (msg.includes('User is not confirmed') || msg.includes('UserNotConfirmedException')) {
        throw new Error('Email ainda não verificado. Verifique sua caixa de entrada.');
      } else if (msg.includes('User is disabled') || msg.includes('UserDisabledException')) {
        throw new Error('Conta desativada. Entre em contato com o administrador.');
      } else if (msg.includes('Password reset required') || msg.includes('PasswordResetRequiredException')) {
        throw new Error('É necessário redefinir sua senha.');
      } else {
        throw new Error('Falha ao fazer login. Verifique suas credenciais.');
      }
    }
  };

  const logout = async () => {
    try {
      await signOut();
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const getAccessToken = async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString() || null;
    } catch (error) {
      console.error('Error getting access token:', error);
      return null;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
